import React from 'react';
import { motion } from 'framer-motion';
import { useProperty } from '../context/PropertyContext';
import toast from 'react-hot-toast';

const PropertyComparison = ({ isOpen, onClose }) => {
  const { 
    getCompareProperties, 
    removeFromCompare, 
    clearCompareList, 
    formatPrice 
  } = useProperty();
  
  const compareProperties = getCompareProperties();

  if (!isOpen) return null;

  const handleRemoveProperty = (propertyId) => {
    removeFromCompare(propertyId);
    toast.success('Property removed from comparison');
  };

  const handleClearAll = () => {
    clearCompareList();
    toast.success('Comparison cleared');
    onClose();
  };

  const comparisonFeatures = [
    { key: 'price', label: 'Price', format: 'price' },
    { key: 'type', label: 'Type', format: 'text' },
    { key: 'bedrooms', label: 'Bedrooms', format: 'number' },
    { key: 'bathrooms', label: 'Bathrooms', format: 'number' },
    { key: 'area', label: 'Area (sq ft)', format: 'number' },
    { key: 'garage', label: 'Garage', format: 'number' },
    { key: 'yearBuilt', label: 'Year Built', format: 'number' },
    { key: 'lotSize', label: 'Lot Size (acres)', format: 'decimal' }
  ];

  const formatValue = (value, format) => {
    if (!value && value !== 0) return 'N/A';
    
    switch (format) {
      case 'price':
        return formatPrice(value);
      case 'number':
        return value.toLocaleString();
      case 'decimal':
        return value.toFixed(1);
      default:
        return value;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="comparison-modal"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="comparison-header">
          <h2>Property Comparison</h2>
          <div className="comparison-actions">
            <button className="btn btn-secondary" onClick={handleClearAll}>
              Clear All
            </button>
            <button className="modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {compareProperties.length === 0 ? (
          <div className="empty-comparison">
            <i className="fas fa-balance-scale"></i>
            <h3>No Properties to Compare</h3>
            <p>Add properties to comparison to see them here</p>
          </div>
        ) : (
          <div className="comparison-content">
            <div className="comparison-table">
              {/* Property Images and Basic Info */}
              <div className="comparison-row property-headers">
                <div className="comparison-label"></div>
                {compareProperties.map((property) => (
                  <div key={property.id} className="comparison-property">
                    <button
                      className="remove-property"
                      onClick={() => handleRemoveProperty(property.id)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                    <img src={property.images[0]} alt={property.title} />
                    <h4>{property.title}</h4>
                    <p className="property-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {property.location}
                    </p>
                  </div>
                ))}
              </div>

              {/* Comparison Features */}
              {comparisonFeatures.map((feature) => (
                <div key={feature.key} className="comparison-row">
                  <div className="comparison-label">{feature.label}</div>
                  {compareProperties.map((property) => (
                    <div key={property.id} className="comparison-value">
                      {formatValue(property[feature.key], feature.format)}
                    </div>
                  ))}
                </div>
              ))}

              {/* Features Comparison */}
              <div className="comparison-row">
                <div className="comparison-label">Features</div>
                {compareProperties.map((property) => (
                  <div key={property.id} className="comparison-value">
                    <div className="features-list">
                      {property.features?.slice(0, 4).map((feature, index) => (
                        <span key={index} className="feature-tag">
                          {feature}
                        </span>
                      ))}
                      {property.features?.length > 4 && (
                        <span className="feature-more">
                          +{property.features.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status */}
              <div className="comparison-row">
                <div className="comparison-label">Status</div>
                {compareProperties.map((property) => (
                  <div key={property.id} className="comparison-value">
                    <span className={`status ${property.status.toLowerCase().replace(' ', '-')}`}>
                      {property.status}
                    </span>
                  </div>
                ))}
              </div>

              {/* Agent */}
              <div className="comparison-row">
                <div className="comparison-label">Agent</div>
                {compareProperties.map((property) => (
                  <div key={property.id} className="comparison-value">
                    <div className="agent-info">
                      <img src={property.agent.image} alt={property.agent.name} />
                      <div>
                        <p className="agent-name">{property.agent.name}</p>
                        <p className="agent-title">{property.agent.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="comparison-footer">
              <p>Compare up to 3 properties side by side</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PropertyComparison;